import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from './ui/Card';
import { Wallet } from 'lucide-react';

interface BalanceResp {
  usd: number | null;
  asOf: string | null;
}

const USD_TO_RUB = 90;

// Локальная утилита (не экспортируется), чтобы файл экспортировал только
// компонент — иначе react-refresh/only-export-components ломает fast refresh.
function costInRub(usd: number): string {
  return Math.round(usd * USD_TO_RUB) + ' ₽';
}

/**
 * Виджет баланса FAL + оценка стоимости текущей генерации.
 * Используется в Content/Photo Studio.
 */
export function BalanceWidget({ estimatedUsd }: { estimatedUsd?: number }) {
  const { data } = useQuery<BalanceResp>({
    queryKey: ['fal-balance-widget'],
    queryFn: () => api.get<BalanceResp>('/api/studios/balance'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const usd = data?.usd;
  const balanceRub = usd != null ? Math.round(usd * USD_TO_RUB) : null;
  const isLow = usd != null && usd < 5;

  return (
    <Card
      className={
        'flex items-center justify-between gap-3 ' +
        (isLow ? 'border-2 border-red-500/40 bg-red-500/5' : '')
      }
    >
      <div className="flex items-center gap-2">
        <Wallet className={`w-5 h-5 ${isLow ? 'text-red-500' : 'text-brand-orange'}`} />
        <div>
          <div className="text-xs text-tg-hint">FAL баланс</div>
          {usd != null ? (
            <div className="font-semibold">
              ${usd.toFixed(2)}{' '}
              <span className="text-tg-hint text-sm">(~{balanceRub} ₽)</span>
            </div>
          ) : (
            <div className="text-sm text-tg-hint">недоступно</div>
          )}
        </div>
      </div>
      {estimatedUsd != null && estimatedUsd > 0 && (
        <div className="text-right">
          <div className="text-xs text-tg-hint">Стоимость ~</div>
          <div className="font-semibold text-brand-orange">{costInRub(estimatedUsd)}</div>
        </div>
      )}
    </Card>
  );
}
